import React from 'react'
import styled from 'styled-components'

import defaultHero from '../../../assets/default-hero-image.jpg'
import { StyledTheme } from '../../../theme'
import { ThemeColor } from '../../../theme/colors'
import { Ellipsis } from '../typography'

export type HeaderSize = keyof StyledTheme['header']

export interface HeaderProps {
  image?:
    | string
    | {
        file: string
      }
  size?: HeaderSize
  overlayColor?: ThemeColor
  overlayOpacity?: number
  useTallOnMobile?: boolean
  children?: React.ReactNode
}

const StyledHeader = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    prop !== 'hasDefault',
})<{
  $size: HeaderSize
  $image: string
  $useTallOnMobile: boolean
}>`
  position: relative;
  height: ${({ theme, $size }) => theme.header[$size]};
  background-image: url(${({ $image }) => $image});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  h1 {
    margin: 0 ${({ theme }) => theme.space.s12};

    ${Ellipsis};
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;

    ${({ theme }) => theme.mediaQuery.screenM} {
      -webkit-line-clamp: ${({ $useTallOnMobile }) => $useTallOnMobile && 3};
      font-size: ${({ theme }) => theme.space.s48};
      line-height: ${({ theme }) => theme.space.s64};
    }

    ${({ theme }) => theme.mediaQuery.screenS} {
      -webkit-line-clamp: ${({ $useTallOnMobile }) =>
        $useTallOnMobile ? 3 : 5};
      font-size: ${({ theme }) => theme.space.s40};
      line-height: ${({ theme }) => theme.space.s48};
    }

    ${({ theme }) => theme.mediaQuery.tablet} {
      -webkit-line-clamp: ${({ $useTallOnMobile }) =>
        $useTallOnMobile ? 4 : 6};
      font-size: ${({ theme }) => theme.space.s32};
      line-height: ${({ theme }) => theme.space.s40};
    }

    ${({ theme }) => theme.mediaQuery.smallPhone} {
      -webkit-line-clamp: ${({ $useTallOnMobile }) =>
        $useTallOnMobile ? 3 : 5};
      font-size: ${({ theme, $useTallOnMobile }) =>
        $useTallOnMobile && theme.space.s24};
      line-height: ${({ theme, $useTallOnMobile }) =>
        $useTallOnMobile && theme.space.s32};
    }
  }

  ${({ theme }) => theme.mediaQuery.tablet} {
    height: ${({ theme, $useTallOnMobile, $size }) =>
      $useTallOnMobile ? theme.header.tall : theme.header[$size]};
  }
`

const Overlay = styled.div<{ $color: ThemeColor; $opacity: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: ${({ theme, $color }) => theme.color[$color]};
  opacity: ${({ $opacity }) => $opacity};
`

const HeaderContent = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  padding: ${({ theme }) => theme.space.s12};

  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`

export const Header: React.FC<HeaderProps> = ({
  image = defaultHero,
  size = 'medium',
  overlayColor = 'black',
  overlayOpacity = 0.35,
  children,
  useTallOnMobile = false,
  ...props
}) => {
  return (
    <StyledHeader
      $useTallOnMobile={useTallOnMobile}
      $size={size}
      $image={image}
      {...props}
    >
      <Overlay $color={overlayColor} $opacity={overlayOpacity} />
      <HeaderContent>{children}</HeaderContent>
    </StyledHeader>
  )
}
